import { HttpStatusCode } from "axios";
import store from "../../../app/store";
import { setLoaderFalse, setLoaderTrue } from "../../../Redux/loaderSlice";
import { API_STATUS } from "../sliceUtil";
import { CustomError } from "../../../api/CustomError";
import { CustomSuccess } from "../../../api/CustomSuccess";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _delete, _get, _put } from "../../../api/apiClient";

export const _orgClientId = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.orgClientId
    ? state?.orgReducer?.selectedFI?.orgClientId
    : state?.authReducer?.userData?.client_Id;
};

const initialPaymentHistoryState = {
  activeSubscriptionDetails: null,
  activeSubscriptionDetailsLoading: API_STATUS.STATUS_IDLE,

  consultantSubscriptionDetails: null,
  consultantSubscriptionDetailsLoading: API_STATUS.STATUS_IDLE,

  paymentHistoryList: null,
  paymentHistoryListLoading: API_STATUS.STATUS_IDLE,
};

export const fetchPaymentHistory = createAsyncThunk(
  "paymentHistory/fetchPaymentHistory",
  async ({ paginationObj, disableToast, disableLoader = false }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Subscription List successfully.",
        errorMessage: "Failed to fetch Subscription List!",
        disableToast: disableToast,
      };

      const _pageNumber = paginationObj?.currentPage
        ? `&PageNumber=${paginationObj?.currentPage}`
        : "";
      const _pageSize =
        paginationObj?.limit > 0 ? `PageSize=${paginationObj?.limit}` : "";

      const _sortOrder = paginationObj?.sortObj?.sortOrder
        ? `&OrderBy=${paginationObj?.sortObj?.sortOrder}`
        : "";
      const _sortField = paginationObj?.sortObj?.sortField
        ? `&SortBy=${paginationObj?.sortObj?.sortField}`
        : "";

      const response = await _get(
        `${_orgClientId()}/payment/history?${_pageSize}${_pageNumber}${_sortField}${_sortOrder}`,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: paymentHistoryList,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        paymentHistoryList,
        paginationObj,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const fetchActiveSubscriptionDetails = createAsyncThunk(
  "paymentHistory/fetchActiveSubscriptionDetails",
  async ({ disableToast, disableLoader = false, is_consultant = false }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Active Subscription Details successfully.",
        errorMessage: "Failed to fetch active subscription details!",
        disableToast: disableToast,
      };

      const response = await _get(
        `${_orgClientId()}/subscription${is_consultant ? "?is_consultant=true" : ""
        }`,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: activeSubscriptionDetails,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        activeSubscriptionDetails,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const fetchPaymentMethods = createAsyncThunk(
  "paymentHistory/fetchPaymentMethods",
  async ({ paginationObj, disableToast, disableLoader = false }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Payment Methods successfully.",
        errorMessage: "Failed to fetch Payment Methods!",
        disableToast: disableToast,
      };

      const response = await _get(`/payment/method`);

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: paymentMethodList,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        paymentMethodList,
        paginationObj,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const addPaymentMethods = createAsyncThunk(
  "paymentHistory/addPaymentMethods",
  async ({ disableToast, disableLoader = false }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Payment Methods added successfully.",
        errorMessage: "Failed to add Payment Methods!",
        disableToast: disableToast,
      };

      const response = await _get(`/payment/method/link`);

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const removePaymentMethod = createAsyncThunk(
  "paymentHistory/removePaymentMethod",
  async ({ selectedPaymentMethod, disableLoader = false }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Removed Payment Method successfully.",
        errorMessage: "Failed to removed payment method!",
      };
      const response = await _delete(
        `/payment/method?payment_method_id=${selectedPaymentMethod?.paymentMethodId}`,
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const updatedList = await thunkAPI.dispatch(
        fetchPaymentMethods({ disableLoader }),
      );
      const _response = {
        ...CustomSuccess(response, config),
        paymentMethodList: updatedList?.payload?.paymentMethodList,
      };
      thunkAPI.dispatch(setLoaderFalse());
      return _response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const cancelSubscription = createAsyncThunk(
  "paymentHistory/cancelSubscription",
  async (
    { activeSubscriptionDetails, disableToast, disableLoader = false },
    thunkAPI,
  ) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Subscription canceled successfully.",
        errorMessage: "Failed to cancel Subscription!",
        disableToast: disableToast,
      };

      const response = await _put(
        `${_orgClientId()}/subscription/cancel?sub_id=${activeSubscriptionDetails?.subscriptionId}`,
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        response,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const setAsdefaultPaymentMethod = createAsyncThunk(
  "paymentHistory/setAsdefaultPaymentMethod",
  async ({ selectedPaymentMethod, disableLoader = false }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Payment method set as a default successfully.",
        errorMessage: "Failed to set as a default Payment method!",
      };
      const response = await _put(
        `/payment/method?payment_method_id=${selectedPaymentMethod?.paymentMethodId}`,
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const updatedList = await thunkAPI.dispatch(
        fetchPaymentMethods({ disableLoader }),
      );
      const _response = {
        ...CustomSuccess(response, config),
        paymentMethodList: updatedList?.payload?.paymentMethodList,
      };
      thunkAPI.dispatch(setLoaderFalse());
      return _response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);


export const fetchConsultantSubscription = createAsyncThunk(
  "paymentHistory/fetchConsultantSubscription",
  async ( { disableToast, disableLoader = false, isBook = false }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());

      const config = {
        successMessage: "Fetched Consultant Subscription successfully.",
        errorMessage: "Failed to fetch consultant subscription details!",
        disableToast,
      };

      const response = await _get(
        `${_orgClientId()}/subscription/consultant?isBook=${isBook}`
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: consultantSubscriptionDetails,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(setLoaderFalse());

      return {
        consultantSubscriptionDetails,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (!disableLoader) {
        thunkAPI.dispatch(setLoaderFalse());
      }
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const paymentHistorySlice = createSlice({
  name: "PaymentHistory",
  initialState: initialPaymentHistoryState,
  reducers: {
    resetPaymentHistorySlice: () => {
      return initialPaymentHistoryState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentHistory.pending, (_state) => ({
        ..._state,
        paymentHistoryListLoading: API_STATUS.STATUS_LOADING,
      }))
      .addCase(fetchPaymentHistory.fulfilled, (_state, action) => {
        const { currentPage = 1, limit = 10 } =
          action?.payload?.paginationObj || {};

        const offset = (currentPage - 1) * limit;

        const list = action?.payload?.paymentHistoryList?.data || [];

        const SLData = list.map((item, index) => ({
          ...item,
          srNo: offset + index + 1,
        }));

        return {
          ..._state,
          paymentHistoryListLoading: API_STATUS.STATUS_SUCCESS,
          paymentHistoryList: {
            data: SLData,
            totalCount: action.payload.paymentHistoryList.totalCount,
          },
        };
      })
      .addCase(fetchPaymentHistory.rejected, (_state, action) => ({
        paymentHistoryListLoading: API_STATUS.STATUS_FAILURE,
        message:
          action.error.message || "Error while fetching draft email campaigns",
      }))
      .addCase(fetchActiveSubscriptionDetails.pending, (state) => {
        state.activeSubscriptionDetailsLoading = API_STATUS.STATUS_LOADING;
      })
      .addCase(fetchActiveSubscriptionDetails.fulfilled, (state, action) => {
        state.activeSubscriptionDetailsLoading = API_STATUS.STATUS_SUCCESS;

        const data =
          action.payload?.activeSubscriptionDetails || action.payload;

        state.activeSubscriptionDetails = {
          ...data,
          currentStatus:
            data?.currentStatus?.toLowerCase() === "active"
              ? "Active"
              : data?.currentStatus,
        };
      })

      .addCase(fetchActiveSubscriptionDetails.rejected, (state, action) => {
        state.activeSubscriptionDetailsLoading = API_STATUS.STATUS_FAILURE;
        state.message =
          action?.payload?.message ||
          action.error?.message ||
          "Error while fetching email campaigns summary";
      })

      
      // ============ fetchConsultantSubscription ==========

      .addCase(fetchConsultantSubscription.pending, (state) => {
        state.consultantSubscriptionDetailsLoading =
          API_STATUS.STATUS_LOADING;
      })

      .addCase(fetchConsultantSubscription.fulfilled, (state, action) => {
        state.consultantSubscriptionDetailsLoading =
          API_STATUS.STATUS_SUCCESS;

        state.consultantSubscriptionDetails =
          action.payload?.consultantSubscriptionDetails;
      })

      .addCase(fetchConsultantSubscription.rejected, (state, action) => {
        state.consultantSubscriptionDetailsLoading =
          API_STATUS.STATUS_FAILURE;

        state.message =
          action?.payload?.message ||
          action.error?.message ||
          "Error while fetching consultant subscription details";
      })
  },
});

export const { resetPaymentHistorySlice } = paymentHistorySlice.actions;
